import axios from 'axios';
import { invalidateAuthOnUnauthorized } from './api';

const ratingAPI = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
ratingAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para lidar com erros
ratingAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    invalidateAuthOnUnauthorized(error, 'RatingAPI');
    return Promise.reject(error);
  }
);

export interface Rating {
  id: string;
  appointment_id: string;
  client_id: string;
  employee_id: string;
  property_id: string;
  rating: number; // 1-5
  comment?: string;
  punctuality_rating?: number;
  quality_rating?: number;
  communication_rating?: number;
  cleanliness_rating?: number;
  status: 'pending' | 'submitted' | 'approved' | 'rejected';
  employee_response?: string;
  employee_response_at?: string;
  admin_response?: string;
  admin_response_at?: string;
  would_recommend: boolean;
  is_anonymous: boolean;
  created_at: string;
  updated_at: string;
  submitted_at: string;
  // Campos relacionados (populados pelo backend)
  client_name?: string;
  employee_name?: string;
  property_name?: string;
  appointment_date?: string;
}

export interface CreateRatingRequest {
  appointment_id: string;
  rating: number;
  comment?: string;
  punctuality_rating?: number;
  quality_rating?: number;
  communication_rating?: number;
  cleanliness_rating?: number;
  would_recommend?: boolean;
  is_anonymous?: boolean;
}

export interface EmployeeRatingStats {
  employee_id: string;
  employee_name: string;
  average_rating: number;
  total_ratings: number;
  five_star_count: number;
  four_star_count: number;
  three_star_count: number;
  two_star_count: number;
  one_star_count: number;
}

// Criar avaliação
export const createRating = async (data: CreateRatingRequest) => {
  const response = await ratingAPI.post('/ratings', data);
  return response.data;
};

// Obter avaliação por ID
export const getRatingById = async (id: string) => {
  const response = await ratingAPI.get(`/ratings/${id}`);
  return response.data;
};

// Obter avaliações de um funcionário
export const getEmployeeRatings = async (employeeId: string, params?: {
  limit?: number;
  offset?: number;
  status?: string;
}) => {
  const response = await ratingAPI.get(`/ratings/employee/${employeeId}`, { params });
  return response.data;
};

// Obter avaliações de um cliente
export const getClientRatings = async (clientId: string, params?: {
  limit?: number;
  offset?: number;
}) => {
  const response = await ratingAPI.get(`/ratings/client/${clientId}`, { params });
  return response.data;
};

// Obter avaliação de um appointment
export const getAppointmentRating = async (appointmentId: string) => {
  const response = await ratingAPI.get(`/ratings/appointment/${appointmentId}`);
  return response.data;
};

// Obter estatísticas de avaliação de um funcionário
export const getEmployeeRatingStats = async (employeeId: string): Promise<EmployeeRatingStats> => {
  const response = await ratingAPI.get(`/ratings/employee/${employeeId}/stats`);
  return response.data;
};

// Obter ranking de funcionários por avaliação
export const getEmployeeRankings = async (params?: {
  limit?: number;
  min_ratings?: number;
}) => {
  const response = await ratingAPI.get('/ratings/rankings', { params });
  return response.data;
};

// Atualizar avaliação (apenas para admin ou o próprio cliente)
export const updateRating = async (id: string, data: Partial<CreateRatingRequest>) => {
  const response = await ratingAPI.put(`/ratings/${id}`, data);
  return response.data;
};

// Adicionar resposta do funcionário
export const addEmployeeResponse = async (id: string, response: string) => {
  const ratingResponse = await ratingAPI.post(`/ratings/${id}/employee-response`, { response });
  return ratingResponse.data;
};

// Adicionar resposta do administrador
export const addAdminResponse = async (id: string, response: string) => {
  const ratingResponse = await ratingAPI.post(`/ratings/${id}/admin-response`, { response });
  return ratingResponse.data;
};

export default ratingAPI;




















